import { ApiError } from './api';

const TEXT: Record<string, string> = {
  not_signed_in: 'Please sign in again.',
  forbidden: 'You do not have permission to do that.',
  bad_transition: 'That action is not possible in the current state.',
  period_locked: 'That month is locked. Ask an admin to unlock it or use an override.',
  cash_limit: 'Cash to this payee today would cross the section 40A(3) limit. Pay by bank, or ask an admin for an override.',
  unbalanced: 'The entry does not balance.',
  immutable: 'Posted entries cannot be edited. Post a reversal instead.',
  reason_required: 'A reason is required.',
  tds_override_needs_reason: 'Give a reason for overriding the TDS rate.',
  validation_expired: 'This validation link has expired or the invoice has changed since it was sent.',
  bad_token: 'This link is not valid.',
  otp_invalid: 'That code is not right.',
  otp_expired: 'The code has expired. Request a new one.',
  otp_locked: 'Too many attempts. Request a new code.',
  rate_limited: 'Too many requests. Try again in a while.',
  allocation_exceeds_amount: 'The allocations add up to more than the payment.',
  allocation_exceeds_outstanding: 'An allocation is more than what is outstanding on that document.',
  allocation_party_mismatch: 'The document belongs to a different party.',
  allocation_required: 'Pick the bill(s) this payment settles.',
  tds_recovery_exceeds_due: 'A TDS recovery is more than what is still recoverable on that bill.',
  tds_recovery_not_allowed: 'TDS can only be recovered on a payment that settles a bill.',
  tds_recovery_amount_required: 'Enter the TDS amount to recover, or leave the row blank.',
  tds_recovery_duplicate: 'The same bill is listed twice in the TDS recovery.',
  service_exists: 'A service with that name already exists in the catalogue.',
  rate_required: 'Enter a rate of zero or more.',
  interest_note_required: 'The interest differs from what Bahi computed for these deductions. Say why.',
  bad_outcome: 'Pick verified, inoperative or invalid.',
  pan_required: 'Enter the PAN on the party first.',
  date_required: 'Pick the month and the deposit date.',
  agreement_body_required: 'Write the agreement text before sending it for acceptance.',
  bad_quarter: 'Pick a quarter from 1 to 4.',
  bill_not_payable: 'That bill is not validated yet, so it cannot be paid.',
  expense_not_reimbursable: 'That expense cannot be reimbursed (not posted, not paid personally, or already reimbursed).',
  invoice_not_receivable: 'That invoice is not open for receipts.',
  party_required: 'Pick a party.',
  lines_required: 'Add at least one line.',
  amount_required: 'Enter an amount.',
  mode_required: 'Pick a payment mode.',
  money_account_required: 'Pick the bank or cash account.',
  bank_account_required: 'Pick a bank account.',
  cash_account_required: 'Pick a cash account.',
  claimant_required: 'Say who paid.',
  category_required: 'Pick a category.',
  description_required: 'Add a description.',
  vendor_email_required: 'The vendor has no email address. Add one on the party page.',
  attachment_required: 'Attach the file first.',
  file_required: 'Choose a file.',
  self_approval: 'You cannot approve what you created. Ask another approver.',
  no_pending_approval: 'There is nothing pending to approve.',
  nothing_selected: 'Select at least one item.',
  too_many: 'Too many at once. Decide at most 200 at a time.',
  approval_pending: 'This is waiting for approval.',
  approvals_pending: 'There are approvals pending. Decide them first.',
  has_payments: 'Reverse the payments first.',
  reconciled: 'This is reconciled with the bank statement. Unreconcile the statement first.',
  lines_open: 'Some statement lines are still unmatched.',
  balance_mismatch: 'The book balance does not agree with the statement closing balance.',
  amount_mismatch: 'The amounts do not match.',
  undo_window_passed: 'The undo window has passed. Reverse the payment instead.',
  declaration_required: 'Fill in the missing-bill declaration first.',
  nothing_to_reimburse: 'Nothing to reimburse.',
  not_a_partner: 'That party is not a partner.',
  last_admin: 'You cannot remove the last admin.',
  bad_pan: 'That PAN does not look right (five letters, four digits, one letter).',
  account_mapping_missing: 'An account mapping is missing in Settings.',
  number_pattern_missing: 'A number pattern is missing in Settings.',
  not_found: 'Not found.',
  name_required: 'Enter a name.',
  note_required: 'Write a note first.',
  bad_method: 'Pick a validation method.',
  attachment_in_use: 'This file is validation evidence and cannot be removed.',
  reimbursed: 'This expense was already reimbursed; reverse that payment first.',
  nothing_to_close: 'There is nothing to close for that year.',
  external_number_required: 'Enter the platform invoice number.',
  bad_ticket: 'The upload ticket is not valid. Try again.',
  file_not_uploaded: 'The file did not upload. Try again.',
  attachment_locked: 'This file is evidence for a signed record and cannot be removed or replaced.',
  bad_period_kind: 'Pick monthly or quarterly.',
  period_in_future: 'That period has not started yet.',
  nothing_to_acknowledge: 'No cash voucher for this payee and period needs a signature.',
  voucher_not_eligible: 'A voucher picked is not for this payee and period, does not need a signature, or is already on a sheet.',
  concurrent_change: 'The vouchers changed while the sheet was being prepared. Try again.',
  scan_required: 'Upload the signed sheet first (a photo or a scan of the paper).',
  scan_fingerprint_missing: 'The upload has no fingerprint. Upload the file again.',
  scan_is_unsigned_original: 'That is the PDF Bahi printed, not the signed copy. Upload a photo or scan of the signed paper.',
  scan_already_used: 'That exact file is already evidence for another record. Upload the scan of this sheet.',
  signed_on_required: 'Enter the date the payee signed.',
  signed_on_before_issue: 'The payee cannot have signed before the sheet was prepared. Enter the date written on the sheet.',
  signed_on_in_future: 'The date of signing cannot be in the future.',
  revenue_stamp_required: 'The total is above the revenue stamp limit: confirm the ₹1 stamp is affixed and signed across.',
  sheet_items_changed: 'A voucher on this sheet was reversed or changed after it was printed. Void the sheet and issue a new one.',
  payee_already_set: 'This payment already has a payee.',
  cannot_sign_on_screen: 'This voucher cannot be signed on screen.',
  shown_mismatch: 'The voucher changed since it was shown. Open it again and sign afresh.',
  signer_name_required: 'Enter the payee\'s name as they write it.',
  signature_required: 'The payee has not signed yet.',
  signature_mismatch: 'The signature image did not upload intact. Clear and sign again.',
  signature_already_used: 'That signature image is already used. Sign again.',
  signature_not_png: 'The signature must be a PNG image.',
  signature_too_large: 'The signature image is too large. Clear and sign again.',
  already_signed: 'This voucher is already signed; a wrong payment is reversed, not re-signed.',
  signature_fixed: 'The payee\'s acknowledgement cannot be changed. Reverse the payment instead.',
  // the assistant
  ai_disabled: 'The assistant is turned off. An admin can enable it under Settings → Assistant.',
  ai_quota: 'You have used today\'s assistant turns. Try again tomorrow.',
  ai_budget: 'The assistant has reached today\'s spend budget.',
  ai_token_invalid: 'The assistant session expired. Send the message again.',
  ai_bad_attachment: 'That file does not belong to this conversation. Upload it again.',
  ai_file_mismatch: 'The file sent does not match the one stored. Upload it again.',
  ai_kind_not_sendable: 'Files of that kind are never sent to the assistant.',
  ai_entity_not_allowed: 'The assistant may not read that kind of record.',
  plan_changed: 'The proposal changed since the card was shown. Review the latest version.',
  stale_proposal: 'Something this proposal relied on changed. Ask the assistant to draft it again.',
  proposal_expired: 'This proposal expired before it was applied. Ask the assistant to draft it again.',
  second_person_required: 'A second person, an approver or admin other than the first confirmer, must confirm this.',
  op_not_allowed: 'That operation is not in the assistant\'s allow-list.',
  bad_plan: 'The plan is not valid.',
  bad_rule: 'The rule is not valid.',
  rule_conflict: 'Another active rule already covers exactly this case.',
  parameters_required: 'Fill in the rule\'s parameters first.',
  cash_over_limit: 'Cash to this payee that day would be above the section 40A(3) limit.',
  assistant_busy: 'The assistant is busy. Try again in a minute.',
  assistant_error: 'The assistant hit a problem. Try again; if it persists, record the entry by hand.',
  assistant_truncated: 'The model ran out of room while drafting. Try a shorter question.',
  turn_timeout: 'The assistant took too long and stopped. Try again.',
  provider_key_missing: 'No model provider key is configured on the server.',
  provider_model_unavailable: 'The provider does not offer that model to this key. Pick another under Settings → Assistant → Provider and models.',
  provider_misconfigured: 'The assistant is not fully set up. An admin can finish it under Settings → Assistant.',
  turn_stopped: 'You stopped this answer.',
  bad_setting: 'That setting value is not allowed.',
  provider_key_rejected: 'The model provider rejected the key. Check it under Settings → Assistant.',
  provider_unavailable: 'The model provider is unavailable. Try again later; Bahi works without the assistant.',
  provider_rate_limited: 'The model provider is rate limiting. Try again in a minute.',
  unsupported_file: 'Only PDF, JPEG, PNG and WebP files are read.',
  payload_too_large: 'That upload is too large (10 MB per file).',
  file_too_large: 'That file is too large (10 MB per file).',
};

export function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    const t = TEXT[e.code];
    if (t) return e.detail ? `${t} (${e.detail})` : t;
    if (/JWT|jwt expired|refresh_token/i.test(e.code)) return 'Your session expired. Sign in again.';
    return e.detail ? `${e.code}: ${e.detail}` : e.code;
  }
  if (e && typeof e === 'object' && 'message' in e) {
    const m = String((e as any).message);
    if (/Failed to fetch|NetworkError|Load failed/i.test(m)) return 'Cannot reach the server. Check your connection.';
    return TEXT[m] ?? m;
  }
  return String(e);
}
